import { defineStore } from "pinia";
import { ref } from "vue";
import { io } from "socket.io-client";

export const useSocketStore = defineStore("socket", () => {
  const socket = ref(null);
  const isConnected = ref(false);
  const reconnectAttempts = ref(0);
  const maxReconnectAttempts = 10;
  const currentEventId = ref(null);

  const connect = () => {
    if (socket.value?.connected) {
      return;
    }

    const socketUrl = import.meta.env.VITE_SOCKET_URL || "http://localhost:5000";

    socket.value = io(socketUrl, {
      transports: ["websocket", "polling"],
      // Reconnection settings - CRITICAL for reliability
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 1000, // Start with 1 second
      reconnectionDelayMax: 10000, // Max 10 seconds between attempts
      randomizationFactor: 0.5, // Add randomness to prevent thundering herd
      timeout: 20000, // 20 second connection timeout
      upgradeTimeout: 10000 // 10 second upgrade timeout
    });

    socket.value.on("connect", () => {
      isConnected.value = true;
      reconnectAttempts.value = 0;
      console.log("✅ Socket connected");

      // Re-join event room if we were previously in one
      if (currentEventId.value) {
        console.log(`🔄 Re-joining event room: ${currentEventId.value}`);
        socket.value.emit("join_event", { eventId: currentEventId.value });
      }
    });

    socket.value.on("disconnect", (reason) => {
      isConnected.value = false;
      console.log("⚠️ Socket disconnected:", reason);

      // If server disconnected us, manually reconnect
      if (reason === "io server disconnect") {
        console.log("🔄 Server disconnected, attempting to reconnect...");
        socket.value.connect();
      }
    });

    socket.value.on("connect_error", (error) => {
      reconnectAttempts.value++;
      console.error("❌ Socket connection error:", error.message);

      if (reconnectAttempts.value >= maxReconnectAttempts) {
        console.error("❌ Max reconnection attempts reached");
      }
    });

    socket.value.on("reconnect", (attemptNumber) => {
      console.log(`✅ Socket reconnected after ${attemptNumber} attempts`);
      isConnected.value = true;
      reconnectAttempts.value = 0;
    });

    socket.value.on("reconnect_attempt", (attemptNumber) => {
      console.log(`🔄 Socket reconnection attempt ${attemptNumber}`);
    });

    socket.value.on("reconnect_failed", () => {
      console.error("❌ Socket reconnection failed after all attempts");
    });
  };

  const disconnect = () => {
    if (socket.value) {
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
      currentEventId.value = null;
    }
  };

  const joinEvent = (eventId) => {
    currentEventId.value = eventId;
    if (socket.value && socket.value.connected) {
      socket.value.emit("join_event", { eventId });
      console.log(`📍 Joined event room: ${eventId}`);
    }
  };

  const leaveEvent = (eventId) => {
    if (socket.value && socket.value.connected) {
      socket.value.emit("leave_event", { eventId });
    }
    if (currentEventId.value === eventId) {
      currentEventId.value = null;
    }
  };

  const emit = (event, data) => {
    if (socket.value && socket.value.connected) {
      socket.value.emit(event, data);
    }
  };

  const on = (event, callback) => {
    if (socket.value) {
      socket.value.on(event, callback);
    }
  };

  const off = (event, callback) => {
    if (socket.value) {
      socket.value.off(event, callback);
    }
  };

  return {
    socket,
    isConnected,
    connect,
    disconnect,
    joinEvent,
    leaveEvent,
    emit,
    on,
    off
  };
});
